import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  InputAdornment,
  Avatar,
  useTheme,
  CircularProgress,
  Snackbar,
  Alert,
  Typography,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as SuspendIcon,
  Refresh as ResetPasswordIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import { DataGrid } from "@mui/x-data-grid";

import Header from "../components/Header";
import { tokens } from "../theme";
import stringToColor from "../services/stringToColor";
import EditCMTDialog from "../components/CMTComponant/EditCMTDialog";
import ResetPasswordDialog from "../components/CMTComponant/ResetPasswordDialog";
import ConfirmDialog from "../components/common/ComfirmationDialog";
import AddCMTDialog from "../components/CMTComponant/AddCMTDialog";
import axios from "axios";

const CMTManagementPage = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success"); // or "error"
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [compounds, setCompounds] = useState([]);

  useEffect(() => {
    fetchData();
    fetchCompounds();
  }, []);

  const fetchCompounds = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/getCompoundList`,
        { withCredentials: true }
      );
      setCompounds(res.data.compounds || []);
    } catch (err) {
      console.error("Failed to fetch compounds", err);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/getCMT`,
        { withCredentials: true }
      );
      setRows(response.data.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredRows = rows.filter((row) =>
    `${row.fullname} ${row.email} ${row.phone}`
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  const handleEditClick = (user) => {
    setSelectedUser(user);
    setEditDialogOpen(true);
  };

  const handleResetClick = (user) => {
    setSelectedUser(user);
    setResetDialogOpen(true);
  };

  const handleDeleteClick = (user) => {
    setSelectedUser(user);
    setConfirmDialogOpen(true);
  };

  const handleChangeStatus = async (newStatus) => {
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/updateCMT/${selectedUser.id}`,
        { status: newStatus },
        { withCredentials: true }
      );
      if (res.status === 200) {
        fetchData();
        setEditDialogOpen(false);
        setSnackbarMessage("CMT status updated successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      } else {
        throw new Error("Failed to update status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      setSnackbarMessage("Failed to update status.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handlePasswordReset = async (newPassword) => {
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/CMTResetPassword/${selectedUser.id}`,
        { password: newPassword },
        { withCredentials: true }
      );
      if (res.status === 200) {
        setResetDialogOpen(false);
        setSnackbarMessage("Password reset successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      } else {
        console.error("Failed to reset password");
        setSnackbarMessage("Failed to reset password.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("Error resetting password:", error);
      setSnackbarMessage("Failed to reset password.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const res = await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/deleteCMT/${selectedUser.id}`,
        { withCredentials: true }
      );
      fetchData();
      setConfirmDialogOpen(false);

      setSnackbarMessage("CMT deleted successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error deleting user:", error);
      setSnackbarMessage("Error occured while deleting user");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const columns = [
    {
      field: "avatar",
      headerName: "",
      flex: 0.5,
      sortable: false,
      renderCell: (params) => {
        const name = params?.row?.fullname || "U";
        const avatarUrl = params?.row?.avatar_url;

        return avatarUrl ? (
          <Avatar alt={name} src={avatarUrl} className="notranslate" />
        ) : (
          <Avatar sx={{ bgcolor: stringToColor(name) }} className="notranslate">
            {name.charAt(0).toUpperCase()}
          </Avatar>
        );
      },
    },
    {
      field: "fullname",
      headerName: "Full Name",
      flex: 1,
      renderCell: (params) => (
        <Typography className="notranslate">{params.value}</Typography>
      ),
    },
    {
      field: "username",
      headerName: "Username",
      flex: 1,
      renderCell: (params) => (
        <Typography className="notranslate">{params.value}</Typography>
      ),
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      renderCell: (params) => (
        <Typography className="notranslate">{params.value}</Typography>
      ),
    },
    {
      field: "phone",
      headerName: "Phone",
      flex: 1,
      renderCell: (params) => (
        <Typography className="notranslate" dir="ltr">
          {params.value}
        </Typography>
      ),
    },
    {
      field: "communication_method",
      headerName: "Communication",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const method = (params?.row?.communication_method || "").toLowerCase();
        const showWhatsApp = method === "whatsapp" || method === "both";
        const showEmail = method === "email" || method === "both";

        return (
          <div>
            {showWhatsApp && (
              <Chip
                label="WhatsApp"
                color="success"
                size="small"
                className="notranslate"
              />
            )}
            {showEmail && (
              <Chip
                label="Email"
                color="info"
                size="small"
                className="notranslate"
              />
            )}
          </div>
        );
      },
    },
    {
      field: "compound",
      headerName: "Compound",
      flex: 2,
      sortable: false,
      renderCell: (params) => {
        const compound = params?.row?.compound_name;
        const compoundName = params?.row?.compound_name || "N/A";
        const compoundLogo = params?.row?.compound_logo || null;
        const address = params?.row?.compound_address || "N/A";

        return compound ? (
          <Box
            mt={1}
            mb={1}
            display="flex"
            alignItems="center"
            className="notranslate"
          >
            <Box display="flex" alignItems="center" gap={1}>
              {compoundLogo ? (
                <Avatar alt={compoundName} src={compoundLogo} />
              ) : (
                <Avatar sx={{ bgcolor: stringToColor(compoundName) }}>
                  {compoundName.charAt(0).toUpperCase()}
                </Avatar>
              )}
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {compoundName}
                </Typography>
                <Typography variant="body7">( {address} )</Typography>
              </Box>
            </Box>
          </Box>
        ) : (
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2">_</Typography>
          </Box>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => (
        <Chip
          label={
            params?.row?.status === "active"
              ? "Active"
              : params?.row?.status === "inactive"
              ? "Inactive"
              : "Pending"
          }
          color={
            params?.row?.status === "active"
              ? "success"
              : params?.row?.status === "inactive"
              ? "error"
              : "default"
          }
          size="small"
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <>
          <Tooltip title="Edit">
            <IconButton
              color="primary"
              onClick={() => handleEditClick(params.row)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Reset Password">
            <IconButton
              color="secondary"
              onClick={() => handleResetClick(params.row)}
            >
              <ResetPasswordIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              color="error"
              onClick={() => handleDeleteClick(params.row)}
            >
              <SuspendIcon />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <Box className="main-container mt-4">
      <Header title="CMT Management" />

      <Grid container spacing={2} alignItems="center" mb={2}>
        <Grid item xs={12} md={4}>
          <TextField
            variant="outlined"
            placeholder="Search by name, email, or phone"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <IconButton onClick={() => setSearch("")}>
            <RefreshIcon />
          </IconButton>
        </Grid>
        <Grid item xs={12} md={8} display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
          >
            Add CMT
          </Button>
        </Grid>
      </Grid>

      <div style={{ height: "75vh", width: "100%" }}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          getRowId={(row) => row.id}
          pageSize={10}
          loading={loading}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 10,
              },
            },
          }}
          getRowHeight={() => "auto"}
          pageSizeOptions={[5, 10, 15, 20, 25]}
          disableSelectionOnClick
          sx={{
            "& .MuiDataGrid-cell": {
              display: "flex",
              justifyContent: "left",
              alignItems: "center",
            },
            "& .MuiDataGrid-columnHeaders": {
              justifyContent: "center",
              textAlign: "center",
            },
          }}
        />
      </div>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <EditCMTDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        user={selectedUser}
        onChangeStatus={handleChangeStatus}
        onUserUpdate={fetchData}
      />

      <ResetPasswordDialog
        open={resetDialogOpen}
        onClose={() => setResetDialogOpen(false)}
        onReset={handlePasswordReset}
      />

      <ConfirmDialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Confirm Deletion"
        content="Are you sure you want to delete this user?"
      />

      <AddCMTDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        compounds={compounds}
        onSuccess={() => {
          fetchData();
          setSnackbarMessage("CMT created successfully!");
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
        }}
      />
    </Box>
  );
};

export default CMTManagementPage;
